import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result, Typography } from 'antd';
import { CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const NexusLink: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Connecting your Nexus account...');
  const [company, setCompany] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('No link token provided. Please return to Nexus BMP and try again.');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch('/backend/api/auth/nexus-link.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (!data.success) {
          setStatus('error');
          setMessage(data.error || 'Link failed. Token may have expired. Please try again from Nexus BMP.');
          return;
        }

        // Store HR session
        localStorage.setItem('hr_token', data.token);
        localStorage.setItem('hr_tenant_id', data.tenant_id);
        localStorage.setItem('hr_company', data.company);
        localStorage.setItem('hr_plan', data.plan);
        localStorage.setItem('hr_source', 'nexus_linked');

        setCompany(data.company);
        setStatus('success');
        setMessage(data.is_new_tenant
          ? 'HR account created successfully!'
          : 'Welcome back! Reconnected successfully.');

        // Redirect to dashboard after 2 seconds
        setTimeout(() => navigate('/dashboard'), 2000);

      } catch (err) {
        setStatus('error');
        setMessage('Connection error. Please check your internet and try again.');
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', background: '#f0f2f5'
      }}>
        <div style={{
          background: '#fff', borderRadius: 12, padding: 48,
          textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        }}>
          <LinkOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 24 }} />
          <Title level={3}>Connecting to Nexus HR</Title>
          <Text type="secondary">{message}</Text>
          <div style={{ marginTop: 24 }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#f0f2f5'
      }}>
        <Result
          status="error"
          title="Connection Failed"
          subTitle={message}
          extra={[
            <a href="https://nexus.talenthubupskilling.com" key="back">
              Return to Nexus BMP
            </a>
          ]}
        />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#f0f2f5'
    }}>
      <Result
        icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
        title={`Welcome${company ? ', ' + company : ''}!`}
        subTitle={message + ' Redirecting to your HR dashboard...'}
      />
    </div>
  );
};

export default NexusLink;
